import { Link } from 'react-router-dom';
import { useMemo, useState, type ElementType } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    PiUsersBold,
    PiCalculatorBold,
    PiCheckCircleBold,
    PiCurrencyDollarBold,
    PiUsersThreeBold,
    PiTrophyBold,
    PiWarningBold,
} from 'react-icons/pi';
import { PageHeader, LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { hasRole, isSuperAdmin } from '@v2/lib/auth';
import type { CommissionOrgChartData, CommissionPeriodType } from './types';
import CompactOrgChartOverview from './CompactOrgChartOverview';
import OrgChartFilters from './OrgChartFilters';
import { fmtBrl } from './orgChartUtils';

function KpiStripItem({
    icon: Icon,
    label,
    value,
    sub,
}: {
    icon: ElementType;
    label: string;
    value: string;
    sub?: string;
}) {
    return (
        <div className="flex min-w-0 items-center gap-3 border-r border-gray-100 px-4 py-3 last:border-r-0 dark:border-zinc-700">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-600 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                <Icon className="size-4" />
            </div>
            <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
                <p className="truncate text-base font-semibold text-gray-900 dark:text-zinc-100">{value}</p>
                {sub && <p className="truncate text-[11px] text-gray-400">{sub}</p>}
            </div>
        </div>
    );
}

export default function CommissionOrgChartPage() {
    const currentYear = new Date().getFullYear();
    const [period, setPeriod] = useState<CommissionPeriodType>('month');
    const [year, setYear] = useState(currentYear);
    const [index, setIndex] = useState(new Date().getMonth() + 1);
    const [backfillResult, setBackfillResult] = useState<{
        calculated: number;
        pending_before: number;
        has_more: boolean;
    } | null>(null);
    const [cascadeResult, setCascadeResult] = useState<{
        processed: number;
        records_created: number;
        has_more: boolean;
        total_paid: number;
    } | null>(null);

    const qc = useQueryClient();

    const yearOptions = useMemo(
        () => Array.from({ length: 5 }, (_, i) => currentYear - 2 + i),
        [currentYear],
    );

    const { data, isLoading, isError } = useQuery<{ data: CommissionOrgChartData }>({
        queryKey: ['commissioning', 'org-chart', period, year, index],
        queryFn: () =>
            api
                .get('/commissioning/org-chart', {
                    params: { period, year, index: period === 'year' ? 1 : index },
                })
                .then((r) => r.data),
    });

    const backfillMutation = useMutation({
        mutationFn: () => api.post('/commissioning/org-chart/backfill').then((r) => r.data.data),
        onSuccess: (result) => {
            setBackfillResult(result);
            qc.invalidateQueries({ queryKey: ['commissioning', 'org-chart'] });
        },
    });

    const cascadeMutation = useMutation({
        mutationFn: () => api.post('/commissioning/org-chart/backfill-cascade').then((r) => r.data.data),
        onSuccess: (result) => {
            setCascadeResult(result);
            qc.invalidateQueries({ queryKey: ['commissioning', 'org-chart'] });
        },
    });

    const chart = data?.data;

    const onPeriodChange = (next: CommissionPeriodType) => {
        setPeriod(next);
        const now = new Date();
        const defaults: Record<CommissionPeriodType, number> = {
            month: now.getMonth() + 1,
            bimonth: Math.ceil((now.getMonth() + 1) / 2),
            quarter: Math.ceil((now.getMonth() + 1) / 3),
            quadrimester: Math.ceil((now.getMonth() + 1) / 4),
            semester: now.getMonth() + 1 <= 6 ? 1 : 2,
            year: 1,
        };
        setIndex(defaults[next]);
    };

    const canMutate = isSuperAdmin() || hasRole('admin');

    const teamCount = chart?.functional?.manager_branches.length ?? 0;
    const sellerCount = (chart?.functional?.manager_branches ?? []).reduce(
        (acc, b) => acc + b.members.length,
        0,
    );
    const coverage = chart?.coverage;
    const dealsWon = coverage?.deals_won ?? coverage?.distinct_deals_in_records ?? 0;
    const dealsMissing = coverage?.deals_missing_commission ?? 0;
    const cascadeMissing = coverage?.paid_installments_missing_cascade ?? 0;

    return (
        <div className="space-y-5">
            <PageHeader
                title="Relatório Global"
                subtitle={
                    chart?.dating_label
                        ?? 'Hierarquia comercial com comissões por competência do vendedor (pagamento do cliente + 1 mês)'
                }
            >
                <Link
                    to="/commissioning/teams"
                    className="flex shrink-0 items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:border-orange-300 hover:text-orange-600 dark:border-zinc-700 dark:text-zinc-300"
                >
                    <PiUsersBold className="size-4" />
                    Gerenciar equipes
                </Link>
            </PageHeader>

            <OrgChartFilters
                period={period}
                year={year}
                index={index}
                yearOptions={yearOptions}
                periodLabel={chart?.period?.label}
                periodRange={chart?.period ? `${chart.period.from} a ${chart.period.to}` : undefined}
                onPeriodChange={onPeriodChange}
                onYearChange={setYear}
                onIndexChange={setIndex}
            />

            {chart && (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                    <div className="grid grid-cols-1 divide-y divide-gray-100 sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4 dark:divide-zinc-700">
                        <KpiStripItem
                            icon={PiCurrencyDollarBold}
                            label="Total do período"
                            value={fmtBrl(chart.grand_total)}
                            sub={
                                chart.grand_paid != null || chart.grand_pending != null
                                    ? `Pago ${fmtBrl(chart.grand_paid ?? 0)} · Pend. ${fmtBrl(chart.grand_pending ?? 0)}`
                                    : undefined
                            }
                        />
                        <KpiStripItem
                            icon={PiUsersThreeBold}
                            label="Times"
                            value={String(teamCount)}
                            sub={`${sellerCount} vendedor${sellerCount !== 1 ? 'es' : ''}`}
                        />
                        <KpiStripItem
                            icon={PiTrophyBold}
                            label="Deals ganhos"
                            value={String(dealsWon)}
                            sub={
                                coverage
                                    ? `${coverage.deals_with_commission} com comissão`
                                    : undefined
                            }
                        />
                        <KpiStripItem
                            icon={PiCheckCircleBold}
                            label="Cobertura"
                            value={coverage ? `${coverage.deals_coverage_pct}%` : '—'}
                            sub={
                                coverage
                                    ? `Cascade ${coverage.cascade_coverage_pct}%`
                                    : undefined
                            }
                        />
                    </div>
                </div>
            )}

            {coverage && !coverage.is_complete && (
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
                    <PiWarningBold className="mt-0.5 size-4 shrink-0" />
                    <div className="min-w-0 flex-1 space-y-1">
                        <p className="font-semibold">Cobertura incompleta neste período</p>
                        <ul className="list-disc space-y-0.5 pl-4 text-xs text-amber-900/90 dark:text-amber-200/80">
                            {dealsMissing > 0 && (
                                <li>
                                    <strong>{dealsMissing}</strong> deal(s) ganho(s) sem registro de comissão
                                    — não entram no organograma até calcular.
                                </li>
                            )}
                            {cascadeMissing > 0 && (
                                <li>
                                    <strong>{cascadeMissing}</strong> parcela(s) paga(s) sem cascade de gerente/diretor.
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            )}

            {backfillResult && (
                <div className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                    <PiCheckCircleBold className="mt-0.5 size-4 shrink-0" />
                    <span className="flex-1">
                        <strong>{backfillResult.calculated}</strong> comissões calculadas.{' '}
                        {backfillResult.has_more
                            ? `Ainda há deals pendentes — clique em "Calcular pendentes" novamente para continuar.`
                            : 'Nenhum deal pendente restante.'}
                    </span>
                    <button onClick={() => setBackfillResult(null)} className="ml-auto shrink-0 text-green-600 hover:text-green-800" type="button">✕</button>
                </div>
            )}

            {cascadeResult && (
                <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    <PiCheckCircleBold className="mt-0.5 size-4 shrink-0" />
                    <span className="flex-1">
                        Cascade sincronizado: <strong>{cascadeResult.records_created}</strong> registro(s) criado(s) em <strong>{cascadeResult.processed}</strong> parcela(s) processada(s).
                        {cascadeResult.has_more && ' Há mais parcelas — rode novamente para continuar.'}
                    </span>
                    <button onClick={() => setCascadeResult(null)} className="ml-auto shrink-0 text-blue-600 hover:text-blue-800" type="button">✕</button>
                </div>
            )}

            {!chart?.has_teams && chart && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <div className="flex items-center gap-2">
                        <PiWarningBold className="size-4 shrink-0" />
                        <span>Nenhum time de vendas configurado. Defina a hierarquia para ver o organograma completo.</span>
                    </div>
                    <Link
                        to="/commissioning/teams"
                        className="shrink-0 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50"
                    >
                        Configurar times
                    </Link>
                </div>
            )}

            {canMutate && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                        <PiCalculatorBold className="size-4 shrink-0 text-gray-400" />
                        <p className="flex-1 text-xs text-gray-500">
                            Deals ganhos sem comissão calculada não aparecem no organograma.
                            {dealsMissing > 0 ? ` Há ${dealsMissing} pendente(s) neste período.` : ''}
                        </p>
                        <button
                            type="button"
                            onClick={() => backfillMutation.mutate()}
                            disabled={backfillMutation.isPending || cascadeMutation.isPending}
                            className="shrink-0 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-100 disabled:opacity-60"
                        >
                            {backfillMutation.isPending ? 'Calculando…' : 'Calcular pendentes'}
                        </button>
                    </div>

                    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                        <PiCalculatorBold className="size-4 shrink-0 text-gray-400" />
                        <p className="flex-1 text-xs text-gray-500">
                            Parcelas pagas sem cascade de gerente/diretor.
                            {cascadeMissing > 0 ? ` Há ${cascadeMissing} neste período.` : ''}
                        </p>
                        <button
                            type="button"
                            onClick={() => cascadeMutation.mutate()}
                            disabled={cascadeMutation.isPending || backfillMutation.isPending}
                            className="shrink-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                        >
                            {cascadeMutation.isPending ? 'Sincronizando…' : 'Sincronizar cascade'}
                        </button>
                    </div>
                </div>
            )}

            {isLoading && <LoadingState variant="dashboard" />}

            {isError && (
                <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    Não foi possível carregar o organograma.
                </p>
            )}

            {chart?.functional && (
                <CompactOrgChartOverview
                    data={chart.functional}
                    unassigned={chart.unassigned}
                />
            )}

            {chart && chart.unassigned.length > 0 &&
                chart.functional?.pillars.every((p) => p.label !== 'Outros vendedores') && (
                <p className="text-center text-xs text-gray-500">
                    {chart.unassigned.length} vendedor(es) fora da hierarquia — inclua-os nos times de vendas.
                </p>
            )}
        </div>
    );
}
